import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TicketTask, TicketUser } from '@app/models';
import { ToastService } from '@teorema/patron';
import { TcTaskService, TcTicketService } from 'src/app/shared/services';
import { SprintColumnComponent } from '../sprint/sprint-column/sprint-column.component';
import { SprintComponent } from '../sprint/sprint.component';

@Injectable({
  providedIn: 'root',
})
export class TicketDropService {
  dragData?: any;

  constructor(
    private toastService: ToastService,
    private datePipe: DatePipe,
    private tcTicketService: TcTicketService,
    private tcTaskService: TcTaskService
  ) {}

  dragStart(event: any, isBeta: boolean = false) {
    if (event.badge) {
      this.toastService.show({
        color: 'warn',
        message: 'Arraste um chamado de cada vez',
      });
      return;
    }

    if (isBeta) {
      const isDateTitle = this.isDateString(event?.title);
      const rawFinished = event?.ticketTask?.task?.finished;

      const isFinished =
        typeof rawFinished === 'number'
          ? rawFinished === 1
          : typeof rawFinished === 'boolean'
            ? rawFinished
            : false;
      if (
        event?.ticketTask &&
        event?.ticketTask?.task &&
        (isFinished ||
          (isDateTitle && !this.isDatesEqual(event?.ticketTask?.task?.expected_date, event?.title)))
      ) {
        const nextTask = event?.ticketTask?.ticket?.tasks?.find(
          task => !task.finished && this.isDatesEqual(task.expected_date, event?.title)
        );

        if (nextTask) {
          event.ticketTask.task = nextTask;
        } else {
          this.toastService.show({
            color: 'warn',
            message: 'Somente tarefas não concluídas podem ser movidas',
          });
          return;
        }
      }
    }

    this.dragData = event;
  }

  async drop(event: any, component: SprintComponent) {
    if (this.dragData) {
      try {
        let sourceSprint = component.getSprintByTitle(this.dragData.title, false);
        let targetSprint = component.getSprintByTitle(event.title, true);

        if (sourceSprint && targetSprint) {
          //View index
          let sourceUserSprint = sourceSprint[this.dragData.user];
          let targetUserSprint = targetSprint[event.user];
          let isUserTheSame = this.dragData.user === event.user;
          let sourceIndex = this.dragData.index;
          let targetIndex = event.index;

          //Previsão
          let sourceExpectedDate = this.dragData.ticketTask.task!.expected_date;
          let targetExpectedDate = event.ticketTask?.task?.expected_date;

          if (!targetExpectedDate && event.ticketTask?.title) {
            let split = event.ticketTask?.title.split('/');

            if (split.length === 3) {
              targetExpectedDate = split[2] + '-' + split[1] + '-' + split[0] + ' 00:00:00';
            }
          }

          if (!targetExpectedDate && event.title.split('-').length === 3) {
            targetExpectedDate = this.datePipe.transform(
              new Date(event.title),
              'YYYY-MM-dd HH:mm:ss'
            );
          }

          if (!targetExpectedDate) {
            this.toastService.show({
              color: 'warn',
              message: 'Nova data de previsão não específicada',
            });
            return;
          }

          const textConfirmation = `
          Tem certeza que deseja mover o chamado ${this.dragData.ticketTask.ticket.ticket_number_teorema} de
          Sprint: ${
            this.dragData.title.split('-').length == 3
              ? this.datePipe.transform(new Date(this.dragData.title), 'dd/MM/YYYY')
              : this.dragData.title
          }
          Desenvolvedor: ${this.dragData.user}
          Previsão: ${this.datePipe.transform(new Date(sourceExpectedDate), 'dd/MM/YYYY')}
          para
          Sprint: ${event.title}
          Desenvolvedor: ${event.user}
          Previsão: ${this.datePipe.transform(new Date(targetExpectedDate), 'dd/MM/YYYY')}`;

          if (confirm(textConfirmation) == false) {
            return;
          }

          let isDateTheSame = sourceExpectedDate === targetExpectedDate;
          this.dragData.ticketTask.task!.expected_date = targetExpectedDate;
          this.dragData.ticketTask.ticket!.expected_date = targetExpectedDate;
          let devChanged = false;

          let removeSourceTicketTask = () => {
            let ticketTasks =
              this.dragData.parentIndex !== undefined
                ? sourceUserSprint.ticketTasks[this.dragData.parentIndex].ticketTasks
                : sourceUserSprint.ticketTasks;
            ticketTasks.splice(sourceIndex, 1);
          };
          let addTicketTaskToTargetSprint = (index?: number) => {
            removeSourceTicketTask();

            let ticketTasks =
              event.parentIndex !== undefined
                ? targetUserSprint.ticketTasks[event.parentIndex].ticketTasks
                : targetUserSprint.ticketTasks;

            if (index !== undefined) {
              ticketTasks.splice(index, 0, this.dragData.ticketTask);
            } else {
              ticketTasks.push(this.dragData.ticketTask);
            }
          };

          //UserSprint
          if (isUserTheSame) {
            addTicketTaskToTargetSprint(targetIndex);
          } else {
            if (isDateTheSame) {
              //Change user on the same sprint
              if (!event.user) {
                //Usuário está tentando arrastar um ticket para o cabeçalho da mesma sprint
                this.toastService.show({
                  color: 'info',
                  message: 'Nenhuma alteração',
                });
                return;
              }

              let taskDevFilter = component.findOptions.filters?.find(f => f.name === 'taskDev');
              let ticketDevFilter = component.findOptions.filters?.find(
                f => f.name === 'ticketDev'
              );
              let userObj =
                taskDevFilter?.options?.find(u => u.name === event.user) ||
                ticketDevFilter?.options?.find(u => u.name === event.user);

              if (!userObj) {
                this.toastService.show({
                  color: 'error',
                  message: 'Usuário ' + event.user + ' não encontrado',
                });
                return;
              }

              devChanged = true;
              this.dragData.ticketTask.task!.user = userObj;
              addTicketTaskToTargetSprint(targetIndex);
            } else {
              //Change expected_date without changing user
              // OBS: Não permite mudar de usuário e mudar de sprint (expected_date) ao mesmo tempo
              component.fillSprint(targetSprint, this.dragData.user);
              targetUserSprint = targetSprint[this.dragData.user];
              addTicketTaskToTargetSprint();
            }
          }

          //Pontos
          this.refreshUserSprintPoints(sourceUserSprint);
          this.refreshUserSprintPoints(targetUserSprint);

          //Atualizar no back
          let tasks: any = [];
          let tickets: any = [];
          let targetTicketTasks: TicketTask[] = this.collectTicketTasks(
            targetUserSprint.ticketTasks
          );

          for (let i = 0; i < targetTicketTasks.length; i++) {
            let newIndex = i + 1;

            let tt = targetTicketTasks[i];
            tt.task!.view_index = newIndex;
            tt.ticket!.view_index = newIndex;

            if (tt.task?.id) {
              let data: any = {
                id: tt.task?.id,
                view_index: newIndex,
                expected_date: tt.task?.expected_date,
              };

              if (devChanged && tt.task?.id === this.dragData.ticketTask.task!.id) {
                data.user_id = tt.task!.user?.id;
              }

              tasks.push(data);
              tickets.push({
                id: tt.ticket?.id,
                expected_date: this.tcTaskService.getLastExpectedDate(tt.ticket),
              });
            } else {
              let data: any = {
                id: tt.ticket?.id,
                view_index: newIndex,
                expected_date: tt.task?.expected_date,
              };

              if (devChanged && tt.ticket?.id === this.dragData.ticketTask.ticket!.id) {
                data.developer_id = tt.task!.user?.id;

                if (!tt.ticket!.ticketUsers) {
                  tt.ticket!.ticketUsers = [];
                }

                let index = tt.ticket!.ticketUsers.findIndex(tu => tu.user_type === 'D');
                if (index > -1) {
                  tt.ticket!.ticketUsers.splice(index, 1);
                }

                tt.ticket!.ticketUsers.push({
                  user_type: 'D',
                  user: tt.task!.user,
                  user_id: tt.task!.user?.id,
                  ticket_id: tt.ticket?.id,
                } as TicketUser);
              }

              tickets.push(data);
            }
          }

          this.dragData = undefined;
          component.sprintColumnComponents.forEach(
            (sprintColumnComponent: SprintColumnComponent) => {
              sprintColumnComponent.calculateTotalPoints();
            }
          );

          if (tasks.length) {
            component.setLoading(true);
            let response = await this.tcTaskService.patchMultiple(tasks).catch(e => {
              console.error(e);
              this.toastService.show({
                color: 'error',
                message: 'Houve um erro ao atualizar as tarefas',
              });
            });
            component.setLoading(false);

            if (response?.length) {
              this.toastService.show({
                color: 'success',
                message: 'Tarefas atualizadas com sucesso',
              });
            }
          }

          if (tickets.length) {
            component.setLoading(true);
            let response = await this.tcTicketService.patchMultiple(tickets).catch(e => {
              console.error(e);
              this.toastService.show({
                color: 'error',
                message: 'Houve um erro ao atualizar os chamados',
              });
            });
            component.setLoading(false);

            if (response?.length) {
              this.toastService.show({
                color: 'success',
                message: 'Chamados atualizados com sucesso',
              });
            }
          }

          //Refresh sprints
          let scrollPosition = {
            x: component.kanbanContainer.nativeElement.scrollLeft,
            y: component.kanbanContainer.nativeElement.scrollTop,
          };
          const nextSprints = this.filterNonEmptyTicketTasksSprint(component.sprints);
          const nextNotPunctuated = this.filterNonEmptyTicketTasksOthers(
            component.notPunctuatedSprint
          );
          const nextPunctuated = this.filterNonEmptyTicketTasksOthers(component.punctuatedSprint);
          const nextDelayedFinished = this.filterNonEmptyTicketTasksOthers(
            component.delayedAndFinishedSprint
          );
          const nextDelayed = this.filterNonEmptyTicketTasksOthers(component.delayedSprint);

          component.sprints = nextSprints;
          component.notPunctuatedSprint = nextNotPunctuated;
          component.punctuatedSprint = nextPunctuated;
          component.delayedAndFinishedSprint = nextDelayedFinished;
          component.delayedSprint = nextDelayed;
          component.syncBoardState();

          requestAnimationFrame(() => {
            component.kanbanContainer.nativeElement.scrollTo(scrollPosition.x, scrollPosition.y);
          });
        }
      } catch (e) {
        this.toastService.show({
          color: 'error',
          message: 'Ocorreu um erro',
        });

        throw e;
      } finally {
        this.dragData = undefined;
      }
    }
  }

  filterNonEmptyTicketTasksOthers(obj): any {
    const result = {};

    for (const key in obj) {
      const sprintUser = obj[key];

      if (Array.isArray(sprintUser.ticketTasks) && sprintUser.ticketTasks.length > 0) {
        result[key] = sprintUser;
      }
    }
    return result;
  }

  filterNonEmptyTicketTasksSprint(obj): any {
    const result = {};

    for (const dateKey in obj) {
      const users = obj[dateKey];
      const filteredUsers = {};

      for (const userKey in users) {
        const userData = users[userKey];

        if (Array.isArray(userData.ticketTasks) && userData.ticketTasks.length > 0) {
          filteredUsers[userKey] = userData;
        }
      }

      if (Object.keys(filteredUsers).length > 0) {
        result[dateKey] = filteredUsers;
      }
    }

    return result;
  }

  dragEnd(event: any) {
    // console.log('dragEnd');
    // console.log(event);
    // this.dragData = undefined;
  }

  refreshUserSprintPoints(userSprint) {
    userSprint.estimatedEffort = 0;
    userSprint.finishedPoints = 0;

    userSprint.ticketTasks.forEach((ticketTask: TicketTask) => {
      if (ticketTask.ticketTasks) {
        let estimatted = 0;
        let finished = 0;

        ticketTask.ticketTasks.forEach((ticketTask: TicketTask) => {
          if (ticketTask.task?.estimated_effort) {
            estimatted += ticketTask.task?.estimated_effort!;

            if (ticketTask.task?.finished) {
              finished += ticketTask.task?.estimated_effort!;
            }
          }
        });

        userSprint.estimatedEffort += estimatted;
        userSprint.finishedPoints += finished;
      } else {
        userSprint.estimatedEffort += ticketTask.task?.estimated_effort;

        if (ticketTask.task?.finished) {
          userSprint.finishedPoints += ticketTask.task?.estimated_effort;
        }
      }
    });
  }

  collectTicketTasks(ticketTasks: TicketTask[]): TicketTask[] {
    let newTicketTasks: TicketTask[] = [];

    ticketTasks.forEach((ticketTask: TicketTask) => {
      if (ticketTask.ticketTasks) {
        newTicketTasks.push(...ticketTask.ticketTasks);
      } else {
        newTicketTasks.push(ticketTask);
      }
    });

    return newTicketTasks;
  }

  isDateString(value: string): boolean {
    const date = new Date(value);
    return !isNaN(date.getTime());
  }

  isDatesEqual(dateStr1: string, dateStr2: string): boolean {
    const date1 = new Date(dateStr1);
    const date2 = new Date(dateStr2);

    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }
}
